<script setup>
import IndexNavbar from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import {onMounted, ref, computed} from "vue";
import {useRoute, useRouter} from 'vue-router'
import Plotly from 'plotly.js-dist'
import {ElLoading, ElMessage} from 'element-plus'
import * as d3 from "d3";
//分页器

// 默认显示第几页
const currentPage = ref(1)
// 个数选择器（可修改）
let pageSizes = [5, 10, 20, 50, 100, 200, 300, 400]
// 默认每页显示的条数（可修改）
const PageSize = ref(5)

// 显示第几页
function handleCurrentChange(val) {
  // 改变默认的页数
  currentPage.value = val
}

function handleSizeChange(val) {
  // 改变每页显示的条数
  PageSize.value = val
  // 注意：在改变每页显示的条数时，要将页码显示到第一页
  currentPage.value = 1
}

const expressionData = ref([])

async function fetchData() {
  try {
    const res = await $axios.get(`http://bioinfo-sysu.com:8282/api/get_expression?input=${geneid.value}`)
    expressionData.value = res.data || []
    drawBoxPlot()
  } catch (err) {
    console.error(err)
    ElMessage.error('Failed to fetch data')
  }
}

function drawBoxPlot() {
  if (!expressionData.value || expressionData.value.length === 0) {
    Plotly.purge('boxPlot')
    return
  }

  const filtered = expressionData.value.filter(d => d.expression !== null && d.expression !== undefined && !isNaN(d.expression));
  const tissuesAll = expressionData.value.map(d => d.tissue);
  const uniqueTissues = Array.from(new Set(tissuesAll));
  const tissueCount = uniqueTissues.length;

  // 获取容器和父容器
  const container = document.getElementById('boxPlot');
  const wrapper = document.getElementById('boxPlotWrapper') || container.parentElement;

  // 使用父容器的实际宽度，确保图表填满可用空间
  const parentWidth = wrapper.clientWidth;
  const parentHeight = 600; // 固定高度

  // 设置容器尺寸为父容器尺寸
  container.style.width = '100%';
  container.style.height = parentHeight + 'px';

  // 根据父容器宽度动态计算字体大小
  const baseFontSize = Math.max(14, parentWidth / 80); // 基础字体大小
  const titleFontSize = baseFontSize * 1.3;
  const axisTitleFontSize = baseFontSize * 1.1;
  const tickFontSize = baseFontSize * 0.9;
  const legendFontSize = baseFontSize * 0.8;

  const colors = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
    '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
    '#c49c94', '#f7b6d2', '#c7c7c7', '#dbdb8d', '#9edae5'
  ];

  const traces = uniqueTissues.map((tissue, idx) => {
    const subset = filtered.filter(d => d.tissue === tissue);
    const color = colors[idx % colors.length];

    return {
      y: subset.map(d => d.expression),
      x: subset.map(_ => tissue),
      text: subset.map(d => d.sample),
      type: 'box',
      name: tissue,
      boxpoints: 'all',
      jitter: 0.3,
      pointpos: 0,
      width: Math.max(0.3, 0.7 - tissueCount * 0.01), // 根据组织数量动态调整宽度
      marker: {
        size: Math.max(3, 6 - tissueCount * 0.1),
        color: color,
        opacity: 0.6,
        line: {width: 0.5, color: 'darkgray'}
      },
      line: {
        width: 2,
        color: color
      },
      fillcolor: 'rgba(255,255,255,0)',
      opacity: 0.8,
      hovertemplate:
          '<b>Sample:</b> %{text}<br>' +
          '<b>Tissue:</b> %{x}<br>' +
          '<b>Expression:</b> %{y:.3f}<extra></extra>'
    };
  });

  // 动态调整标签角度
  let tickAngle = -30;
  if (tissueCount > 15) tickAngle = -45;
  if (tissueCount > 25) tickAngle = -60;
  if (tissueCount > 40) tickAngle = -75;

  // 动态计算右侧边距（为图例留空间）
  const rightMargin = tissueCount > 10 ? 180 : 150;

  const layout = {
    width: parentWidth, // 关键：设置图表宽度为父容器宽度
    height: parentHeight,
    font: {
      size: baseFontSize,
      family: 'Arial, sans-serif'
    },
    title: {
      text: `Bulk tissue gene expression — ${geneid.value}`, // 简化标题，与图片一致
      font: {
        size: titleFontSize,
        weight: 'bold'
      },
      x: 0.5,
      xanchor: 'center',
      y: 0.98
    },
    yaxis: {
      title: {
        text: 'Expression', // 与图片一致
        font: {
          size: axisTitleFontSize,
          weight: 'bold'
        }
      },
      tickfont: {
        size: tickFontSize
      },
      zeroline: true,
      zerolinewidth: 1,
      zerolinecolor: '#ccc',
      gridcolor: '#f0f0f0',
      showgrid: true,
      automargin: true
    },
    xaxis: {
      title: {
        text: 'Tissues', // 与图片一致
        font: {
          size: axisTitleFontSize,
          weight: 'bold'
        }
      },
      tickangle: tickAngle,
      tickfont: {
        size: tickFontSize
      },
      automargin: true,
      showgrid: false
    },
    boxmode: 'group',
    boxgap: Math.max(0.05, 0.3 - tissueCount * 0.005), // 动态调整间距
    boxgroupgap: Math.max(0.05, 0.2 - tissueCount * 0.003),
    // 优化边距
    margin: {
      t: 70,
      b: Math.max(80, 40 + tissueCount * 1.5), // 底部边距根据组织数量调整
      l: 70,
      r: rightMargin
    },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
    showlegend: true,
    legend: {
      orientation: 'v',
      x: 1.02,
      y: 1,
      xanchor: 'left',
      yanchor: 'top',
      bgcolor: 'rgba(255,255,255,0.9)',
      bordercolor: '#ccc',
      borderwidth: 1,
      font: {
        size: legendFontSize
      },
      itemwidth: 30
    },
    hovermode: 'closest',
    autosize: false // 禁用自动调整，使用我们设置的固定尺寸
  };

  const config = {
    responsive: false, // 禁用响应式，使用固定尺寸
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    toImageButtonOptions: {
      format: 'png',
      filename: `GeneBoxPlot_${geneid.value}`,
      width: parentWidth,
      height: parentHeight,
      scale: 2
    }
  };

  // 清空并重新绘制
  container.innerHTML = '';

  Plotly.newPlot('boxPlot', traces, layout, config).then(() => {
    // 绘制完成后检查是否需要调整
    setTimeout(() => {
      Plotly.Plots.resize('boxPlot');
    }, 100);
  });
}

// 添加窗口大小变化监听，确保图表始终填满容器
window.addEventListener('resize', function () {
  if (typeof drawBoxPlot === 'function') {
    setTimeout(drawBoxPlot, 200);
  }
});


import {onBeforeRouteUpdate, onBeforeRouteLeave} from 'vue-router'

let route = useRoute()
let router = useRouter()
// 检查gene参数是否存在
if (!route.query.gene) {
  router.replace({
    path: '/',
  })
}

// 防止用户在当前页面删除参数
onBeforeRouteUpdate((to, from, next) => {
  if (!to.query.gene) {
    // 保留其他参数，添加gene参数
    router.replace({
      ...to,
      query: {
        ...to.query,
        gene: from.query.gene || 'default'
      }
    })
  } else {
    next()
  }
})

// 可选：添加路由离开守卫
onBeforeRouteLeave((to, from, next) => {
  // 可以在这里添加离开前的逻辑
  // 例如保存数据或确认离开
  next()
})
//axios请求
import {inject} from 'vue'
import {ArrowRight} from '@element-plus/icons-vue'

const activePanels = ref(['geneInfo', 'geneExpression', 'swissprot', 'pfam', 'eggnog'])
onMounted(() => {
  const query = route.query
  if (query.gene) {
    geneid.value = query.gene
    showRnaseq()
    showRnaseq2()
    fetchData()
  }
});
const geneid = ref('')
const $axios = inject('$axios')
const rna_seqList = ref([])
const rna_seqList2 = ref([])
const egggolist = ref([])
const pfamgolist = ref([])
const swiss1golist = ref([])
const swiss2golist = ref([])

function showRnaseq() {
  $axios.get('http://bioinfo-sysu.com:8282/api/Gene_search?input=' + geneid.value).then((resp) => {
    if (JSON.parse(JSON.stringify(resp.data)).respCode === '000000') {
      rna_seqList.value = JSON.parse(JSON.stringify(resp.data))['list']
    }
  }).catch((err) => {
    console.log(err)
  })
}

function showRnaseq2() {
  const loadingInstance = ElLoading.service({
    fullscreen: true,
    text: 'Loading',
    background: 'rgba(0, 0, 0, 0.7)',
    lock: true
  })
  $axios.get('http://bioinfo-sysu.com:8282/api/Genego_search?input=' + geneid.value).then((resp) => {
    if (JSON.parse(JSON.stringify(resp.data)).respCode === '000000') {
      rna_seqList2.value = JSON.parse(JSON.stringify(resp.data))['list']
      console.log(rna_seqList2.value)
      $axios.get('http://bioinfo-sysu.com:8282/api/Go_search?input=' + rna_seqList2.value[0]['fields']['go1']).then((resp) => {
        if (JSON.parse(JSON.stringify(resp.data)).respCode === '000000') {
          egggolist.value = JSON.parse(JSON.stringify(resp.data))['list']
          $axios.get('http://bioinfo-sysu.com:8282/api/Go_search?input=' + rna_seqList2.value[0]['fields']['go']).then((resp) => {
            if (JSON.parse(JSON.stringify(resp.data)).respCode === '000000') {
              pfamgolist.value = JSON.parse(JSON.stringify(resp.data))['list']
              $axios.get('http://bioinfo-sysu.com:8282/api/Go_search?input=' + rna_seqList2.value[0]['fields']['go_blastx']).then((resp) => {
                if (JSON.parse(JSON.stringify(resp.data)).respCode === '000000') {
                  swiss1golist.value = JSON.parse(JSON.stringify(resp.data))['list']
                  $axios.get('http://bioinfo-sysu.com:8282/api/Go_search?input=' + rna_seqList2.value[0]['fields']['go_blastp']).then((resp) => {
                    if (JSON.parse(JSON.stringify(resp.data)).respCode === '000000') {
                      swiss2golist.value = JSON.parse(JSON.stringify(resp.data))['list']
                      loadingInstance.close()
                    }
                  }).catch((err) => {
                    console.log(err)
                  })
                }
              }).catch((err) => {
                console.log(err)
              })
            }
          }).catch((err) => {
            console.log(err)
          })
        }
      }).catch((err) => {
        console.log(err)
      })
    }
  }).catch((err) => {
    console.log(err)
    loadingInstance.close()
  })
}


// const downloadFile = (fileName) => {
//   // 文件路径（假设文件名为data.csv）
//   const filePath = './file/' +fileName// 文件位于public目录下
//
//   // 创建隐藏的可下载链接
//   const link = document.createElement('a')
//   link.href = filePath
//   link.download = fileName // 设置下载文件名
//
//   // 添加到DOM并触发点击
//   document.body.appendChild(link)
//   link.click()
//
//   // 清理DOM
//   document.body.removeChild(link)
// };
</script>
<template>
  <div>
    <index-navbar/>
    <section class="relative w-full h-full py-40 min-h-screen">
      <div class="container mx-auto items-center flex flex-wrap">
        <div class="col-12 my-3">
          <el-breadcrumb :separator-icon="ArrowRight" style="font-size: 22px">
            <el-breadcrumb-item :to="{ path: '/' }"><i class="bi bi-house me-1"></i>Home</el-breadcrumb-item>
            <el-breadcrumb-item>Gene</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="col-12">
          <div class="py-2 border-t border-blueGray-200 text-center">
            <div class="flex flex-wrap justify-center">
              <div class="w-full lg:w-9/12 px-4">
                <p class="text-lg leading-relaxed text-blueGray-700">
                </p>
              </div>
            </div>
          </div>
        </div>
        <!--        <div class="col-md-6">-->
        <!--          <img :src="UMAP_celltype">-->
        <!--        </div>-->
        <!--        <div class="col-md-6">-->
        <!--          <img :src="edit_FeaturePlot">-->
        <!--        </div>-->
        <div class="col-12">
          <div class="row justify-content-start">
              <span class="text-blueGray-500 text-lg font-bold text-left" style="font-size:25px">
                Search gene: {{ geneid }}
               </span>
          </div>
          <div class="gene-dashboard">
            <!-- Gene Information 折叠面板 -->
            <el-collapse v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="geneInfo">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-document"></i>
                    <span>Gene Information</span>
                  </div>
                </template>
                <div class="panel-content">
                  <table class="table table-striped table-bordered table-md align-middle mt-4 gene-info-table"
                         style="text-align: left;table-layout:fixed">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 20%">Gene ID</th>
                      <th scope="col" style="width: 15%">Assembly unit</th>
                      <th scope="col" style="width: 10%">Gene start</th>
                      <th scope="col" style="width: 10%">Gene end</th>
                      <th scope="col" style="width: 10%">Strand</th>
                      <th scope="col" style="width: 20%">Gene info</th>
                      <th scope="col" style="width: 15%">Gene type</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in rna_seqList">
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        <router-link
                            :to="{name:'genedetail',query:{gene:item['pk']}}">
                          <el-link>{{ item['pk'] }}</el-link>
                        </router-link>
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].assembly_unit }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].start }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].end }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].strand }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].info }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].type }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </el-collapse-item>
            </el-collapse>
            <el-collapse v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="geneExpression">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-data-analysis"></i>
                    <span>Gene Expression</span>
                  </div>
                </template>
                <div class="panel-content">
                  <div class="expression-header">
                    <div id="boxPlot" style="width: 100%; height: 600px"></div>

                  </div>
                </div>
              </el-collapse-item>
            </el-collapse>
            <el-collapse v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="swissprot">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-document"></i>
                    <span>Swiss-prot</span>
                  </div>
                </template>
                <div class="panel-content">
                  <span class="text-blueGray-500 text-lg font-bold text-left" style="font-size:15px">Go-blastx:</span>
                  <table class="table table-striped table-bordered table-md align-middle mt-4 gene-info-table"
                         style="text-align: left;table-layout:fixed">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 20%">Homologous protein</th>
                      <th scope="col" style="width: 20%">GO ID</th>
                      <th scope="col" style="width: 40%">GO Name</th>
                      <th scope="col" style="width: 20%">GO Type</th>
                      <!--                      <th scope="col" style="width: 25%">KEGG</th>-->
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in swiss1golist">
                      <!-- 第一列只在第一行显示，并占据整列高度 -->
                      <td v-if="index === 0"
                          :rowspan="swiss1golist.length"
                          style="word-break:break-all; word-wrap:break-word; white-space:inherit; vertical-align: middle;">
                        {{ rna_seqList2[0]['fields'].homologous_protein }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['pk'] }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_name }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_type }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <span class="text-blueGray-500 text-lg font-bold text-left" style="font-size:15px">Go-blastp:</span>
                  <table class="table table-striped table-bordered table-md align-middle mt-4 gene-info-table"
                         style="text-align: left;table-layout:fixed">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 20%">Homologous protein</th>
                      <th scope="col" style="width: 20%">GO ID</th>
                      <th scope="col" style="width: 40%">GO Name</th>
                      <th scope="col" style="width: 20%">GO Type</th>
                      <!--                      <th scope="col" style="width: 25%">KEGG</th>-->
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in swiss2golist">
                      <!-- 第一列只在第一行显示，并占据整列高度 -->
                      <td v-if="index === 0"
                          :rowspan="swiss2golist.length"
                          style="word-break:break-all; word-wrap:break-word; white-space:inherit; vertical-align: middle;">
                        {{ rna_seqList2[0]['fields'].homologous_protein }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['pk'] }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_name }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_type }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </el-collapse-item>
            </el-collapse>
            <el-collapse v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="pfam">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-document"></i>
                    <span>Pfam</span>
                  </div>
                </template>
                <div class="panel-content">
                  <table class="table table-striped table-bordered table-md align-middle mt-4 gene-info-table"
                         style="text-align: left;table-layout:fixed">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 34%">GO ID</th>
                      <th scope="col" style="width: 33%">GO Name</th>
                      <th scope="col" style="width: 33%">GO Type</th>
                      <!--                      <th scope="col" style="width: 25%">KEGG</th>-->
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in pfamgolist">
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['pk'] }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_name }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_type }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </el-collapse-item>
            </el-collapse>
            <el-collapse v-model="activePanels" class="dashboard-collapse my-3">
              <el-collapse-item name="eggnog">
                <template #title>
                  <div class="panel-header">
                    <i class="el-icon-document"></i>
                    <span>Egg-nog</span>
                  </div>
                </template>
                <div class="panel-content">
                  <table class="table table-striped table-bordered table-md align-middle mt-4 gene-info-table"
                         style="text-align: left;table-layout:fixed">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 20%">Orthologous group description</th>
                      <th scope="col" style="width: 20%">GO ID</th>
                      <th scope="col" style="width: 40%">GO Name</th>
                      <th scope="col" style="width: 20%">GO Type</th>
                      <!--                      <th scope="col" style="width: 25%">KEGG</th>-->
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in egggolist.slice((currentPage-1)*PageSize,currentPage*PageSize)">
                      <!-- 第一列只在第一行显示，并占据整列高度 -->
                      <td v-if="index === 0"
                          :rowspan="egggolist.length"
                          style="word-break:break-all; word-wrap:break-word; white-space:inherit; vertical-align: middle;">
                        {{ rna_seqList2[0]['fields'].orthologous_group_description }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['pk'] }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_name }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].go_type }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <el-pagination
                      class="el-pager mt-3"
                      style="--el-color-primary:#303034"
                      background
                      @size-change="handleSizeChange"
                      @current-change="handleCurrentChange"
                      :current-page="currentPage"
                      :page-sizes="pageSizes"
                      :page-size="PageSize"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="egggolist.length">
                  </el-pagination>
                </div>
              </el-collapse-item>
            </el-collapse>
          </div>
        </div>
      </div>

    </section>

    <footer-component/>
  </div>
</template>
<style scoped>
.gene-dashboard {
  max-width: 100%;
  margin: 20px auto;
}

.dashboard-collapse {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.dashboard-collapse :deep(.el-collapse-item) {
  border-bottom: 1px solid #e0e0e0;
}

.dashboard-collapse :deep(.el-collapse-item):last-child {
  border-bottom: none;
}

.dashboard-collapse :deep(.el-collapse-item__header) {
  background-color: #f8f9fa;
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  border: none;
}

.dashboard-collapse :deep(.el-collapse-item__header):hover {
  background-color: #e9ecef;
}

.dashboard-collapse :deep(.el-collapse-item__wrap) {
  background-color: #fff;
  border: none;
}

.dashboard-collapse :deep(.el-collapse-item__content) {
  padding: 0;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-header i {
  color: #409eff;
  font-size: 18px;
}

.panel-content {
  padding: 20px;
}

.gene-info-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.gene-info-table th,
.gene-info-table td {
  padding: 12px 15px;
  text-align: left;
  border: 1px solid #e0e0e0;
}

.gene-info-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #303133;
}

.gene-info-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

.expression-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.expression-header h3 {
  margin: 0;
  color: #303133;
  font-weight: 600;
}


</style>